import React from "react";
import { Table, Button, Form, Input, InputNumber, DatePicker, Select, Card, Row, Col, Space } from "antd";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import moment from "moment";
import useSoftwareAssets from "Hooks/AdminHooks/useSoftwareAssets";
import { useNavigate } from "react-router-dom";

const { TextArea } = Input;

const SoftwareAssets = () => {
  const navigate = useNavigate();
  const {
    softwareAssets,
    employees,
    isLoading,
    handleAddSoftwareAsset,
    columns,
    disablePastDates,
    disableExpiryDate,
    isAdding,
    toggleAddForm,
    form,
    fetchEmployeesByDepartment,
    departments,
  } = useSoftwareAssets();

  return (
    <DashboardLayout>
      <div className="wrapper d-flex flex-column min-vh-100 bg-light">
        <DashboardNavbar />
        <div style={{ padding: "24px" }}>
          {!isAdding ? (
            <Card
              className="card-professional"
              title="Software Assets"
              extra={
                <Space>
                  <Button onClick={() => navigate("/assets")}>Back</Button>
                  <Button type="default" onClick={toggleAddForm}>
                    Add Software Asset
                  </Button>
                </Space>
              }
            >
              <Table
                dataSource={softwareAssets}
                columns={columns}
                className="custom-table"
                rowKey="softwareAssetsId"
                pagination={{ pageSize: 10 }}
              />
            </Card>
          ) : (
            <Card
              className="card-professional"
              title="Add New Software Asset"
              extra={
                <Button onClick={toggleAddForm} type="link">
                  Back to List
                </Button>
              }
            >
              <Form
                form={form}
                onFinish={handleAddSoftwareAsset}
                layout="vertical"
                initialValues={{
                  assetsCost: 0,
                  subscriptionDate: moment(),
                  expiryDate: moment(),
                }}
              >
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="departmentId"
                      label="Department"
                      rules={[{ required: true, message: "Department is required" }]}
                    >
                      <Select
                        placeholder="Select Department"
                        onChange={(deptId) => {
                          form.setFieldValue({
                            employeeId: null,
                          });
                          fetchEmployeesByDepartment(deptId);
                        }}
                      >
                        {departments.map((dept) => (
                          <Select.Option key={dept.id} value={dept.id}>
                            {dept.name}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item
                      name="employeeId"
                      label="Employee"
                      rules={[{ required: true, message: "Employee is required" }]}
                    >
                      <Select placeholder="Select Employee">
                        {employees.map((emp) => (
                          <Select.Option key={emp.id} value={emp.id}>
                            {emp.fullName} ({emp.mobileno})
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="name"
                      label="Asset Name"
                      rules={[{ required: true, message: "Asset Name is required" }]}
                    >
                      <Input placeholder=" Enter Asset Name" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="category"
                      label="Category"
                      rules={[{ required: true, message: "Category is required" }]}
                    >
                      <Input placeholder="Enter Category Name" />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="assetsCost"
                      label="Assets Cost"
                      rules={[
                        { required: true, message: "Assets cost is required" },
                        {
                          type: "number",
                          min: 0,
                          message: "Assets cost must be a positive number",
                        },
                      ]}
                    >
                      <InputNumber
                        min={0}
                        style={{ width: "100%" }}
                        formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                        parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="provider"
                      label="Provider"
                      rules={[{ required: true, message: "Provider is required" }]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="licenseKey"
                      label="License Key"
                      rules={[{ required: true, message: "License key is required" }]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="subscriptionDate"
                      label="Subscription Date"
                      rules={[{ required: true, message: "Subscription date is required" }]}
                    >
                      <DatePicker style={{ width: "100%" }} disabledDate={disablePastDates} />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="expiryDate"
                      label="Expiry Date"
                      rules={[
                        { required: true, message: "Expiry date is required" },
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            const subscriptionDate = getFieldValue("subscriptionDate");
                            if (!value || value.isAfter(subscriptionDate)) {
                              return Promise.resolve();
                            }
                            return Promise.reject(
                              new Error("Expiry date must be after subscription date!")
                            );
                          },
                        }),
                      ]}
                    >
                      <DatePicker style={{ width: "100%" }} disabledDate={disableExpiryDate} />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="notes"
                      label="Notes"
                      rules={[{ required: true, message: "Notes are required" }]}
                    >
                      <TextArea rows={3} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="description"
                      label="Description"
                      rules={[{ required: true, message: "Description is required" }]}
                    >
                      <TextArea rows={3} />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item>
                  <Button disabled={isLoading} className="btn-primary" htmlType="submit">
                    {isLoading ? "Please Wait..." : "Submit"}
                  </Button>
                  <Button className="btn-cancel" style={{ marginLeft: 8 }} onClick={toggleAddForm}>
                    Cancel
                  </Button>
                </Form.Item>
              </Form>
            </Card>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default SoftwareAssets;
